import { useEffect } from "react";

interface SeoHeadProps {
  title: string;
  description: string;
  keywords?: string;
  canonicalPath?: string;
  ogType?: string;
  ogImage?: string;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
  noindex?: boolean;
}

export default function SeoHead({
  title,
  description,
  keywords,
  canonicalPath,
  ogType = "website",
  ogImage,
  jsonLd,
  noindex = false,
}: SeoHeadProps) {
  const siteUrl = (typeof import.meta !== "undefined" && (import.meta as any).env?.VITE_SITE_URL) || "https://cssbuyspreadsheet.link";
  const canonical = canonicalPath ? `${siteUrl}${canonicalPath}` : siteUrl;

  useEffect(() => {
    document.title = title;

    const setMeta = (name: string, content: string, isProperty = false) => {
      const attr = isProperty ? "property" : "name";
      let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };

    setMeta("description", description);
    if (keywords) setMeta("keywords", keywords);

    setMeta("og:title", title, true);
    setMeta("og:description", description, true);
    setMeta("og:url", canonical, true);
    setMeta("og:type", ogType, true);
    if (ogImage) setMeta("og:image", ogImage, true);

    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", title);
    setMeta("twitter:description", description);
    if (ogImage) setMeta("twitter:image", ogImage);

    let link: HTMLLinkElement | null = document.querySelector('link[rel="canonical"]');
    if (!link) {
      link = document.createElement("link");
      link.rel = "canonical";
      document.head.appendChild(link);
    }
    link.href = canonical;

    setMeta("robots", noindex ? "noindex, follow" : "index, follow");

    if (jsonLd) {
      const existingScript = document.getElementById("seo-jsonld");
      if (existingScript) existingScript.remove();

      const script = document.createElement("script");
      script.id = "seo-jsonld";
      script.type = "application/ld+json";

      if (Array.isArray(jsonLd)) {
        script.textContent = JSON.stringify(
          jsonLd.map((item) => ({ ...item, "@context": "https://schema.org" })),
          null,
          2
        );
      } else {
        script.textContent = JSON.stringify(
          { "@context": "https://schema.org", ...jsonLd },
          null,
          2
        );
      }
      document.head.appendChild(script);
    }

    return () => {
      const cleanupScript = document.getElementById("seo-jsonld");
      if (cleanupScript) cleanupScript.remove();
    };
  }, [title, description, keywords, canonical, ogType, ogImage, jsonLd, noindex]);

  return null;
}